import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, createBlock as _createBlock, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps, renderSlot as _renderSlot, normalizeClass as _normalizeClass, resolveDynamicComponent as _resolveDynamicComponent } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "ds-form-field__headline"
}
const _hoisted_2 = ["for", "innerHTML"]
const _hoisted_3 = ["innerHTML"]
const _hoisted_4 = { class: "ds-form-field__main" }
const _hoisted_5 = { class: "ds-form-field__input-container" }
const _hoisted_6 = ["innerHTML", "aria-live"]

import DsTooltip from "../../../tooltip/ds-tooltip.vue";
import { computed, reactive, useSlots } from "vue";


import { Theme } from "../../../types/theme";
import TransitionExpandFade from "@sazka/design-system/src/transitions/transition-expand-fade.vue";
import TransitionFade from "@sazka/design-system/src/transitions/transition-fade.vue";

export interface DsFormFieldProps {
    /**
     * Unique identifier for the form field. This identifier is nessary for accessibility.
     */
    id: string;

    /**
     * Label for the form field.
     */
    label?: string;

    /**
     * Tooltip text for the form field.
     */
    tooltipText?: string;

    /**
     * Supporting text below the label.
     */
    supportingText?: string;

    /**
     * Set error state for the form field. Default is false.
     */
    isError?: boolean;

    /**
     * Error messages related to the form field.
     */
    errorMessage?: string;

    /**
     * Whether the error message should be displayed as a floating error. This does not affect shifting page layout.
     */
    floatingError?: boolean;

    /**
     * Disable the checkbox. Default is false.
     */
    disabled?: boolean;

    /**
     * The input field must be filled out. Default is false.
     */
    required?: boolean;

    /**
     * Theme of the form field. Default is light.
     */
    theme?: Theme;

    /**
     * Indicates whether the component is rendered within Storybook. This prop is needed to DsIcon component for correct rendering.
     */
    isInStorybook?: boolean;
}

export function stripFormFieldProps<T extends DsFormFieldProps>(obj: T): DsFormFieldProps {
    return {
        id: obj.id,
        label: obj.label,
        tooltipText: obj.tooltipText,
        supportingText: obj.supportingText,
        errorMessage: obj.errorMessage,
        floatingError: obj.floatingError,
        disabled: obj.disabled,
        required: obj.required,
        theme: obj.theme,
        isInStorybook: obj.isInStorybook,
    };
}

export default /*@__PURE__*/_defineComponent({
  __name: 'ds-form-field',
  props: {
    id: {},
    label: {},
    tooltipText: {},
    supportingText: {},
    isError: { type: Boolean },
    errorMessage: {},
    floatingError: { type: Boolean },
    disabled: { type: Boolean },
    required: { type: Boolean },
    theme: {},
    isInStorybook: { type: Boolean }
  },
  setup(__props: any) {

const slots = useSlots();

const props = __props;
const isInvalid = computed(() => !!props.errorMessage || props.isError);

const inputClasses = computed(() => {
    const classesArray = ["ds-form-field__input"];

    if (slots.inputLeft) {
        classesArray.push("ds-form-field__input--left");
    }

    if (slots.inputRight) {
        classesArray.push("ds-form-field__input--right");
    }

    return classesArray;
});

const errorTransitionComponent = computed(() => {
    return props.floatingError ? TransitionFade : TransitionExpandFade;
});

const inputLeftAttrs = reactive({
    class: "ds-form-field__input-slot ds-form-field__input-slot--left",
});

const inputAttrs = reactive({
    class: inputClasses,
    ariaInvalid: isInvalid.value ? true : null,
});

const inputRightAttrs = reactive({
    class: "ds-form-field__input-slot ds-form-field__input-slot--right",
});

const inputAfterAttrs = reactive({
    class: "ds-form-field__input-after",
});

const ariaLive = computed(() => (isInvalid.value ? "assertive" : "polite"));

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["ds-form-field", {
            'has-error': isInvalid.value,
            'is-disabled': _ctx.disabled,
            'theme--dark': _ctx.theme == 'dark',
        }])
  }, [
    (_ctx.label || _ctx.tooltipText)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          (_ctx.label)
            ? (_openBlock(), _createElementBlock("label", {
                key: 0,
                class: "ds-form-field__label",
                for: _ctx.id,
                innerHTML: _ctx.label
              }, null, 8, _hoisted_2))
            : _createCommentVNode("", true),
          (_ctx.tooltipText)
            ? (_openBlock(), _createBlock(DsTooltip, {
                key: 1,
                icon: "help",
                placement: "bottom",
                class: "ds-form-field__tooltip",
                "is-in-storybook": _ctx.isInStorybook
              }, {
                default: _withCtx(() => [
                  _createElementVNode("div", {
                    class: "ds-form-field__tooltip-content",
                    innerHTML: _ctx.tooltipText
                  }, null, 8, _hoisted_3)
                ]),
                _: 1
              }, 8, ["is-in-storybook"]))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_4, [
      _createElementVNode("div", _hoisted_5, [
        _renderSlot(_ctx.$slots, "inputLeft", _normalizeProps(_guardReactiveProps(inputLeftAttrs))),
        _renderSlot(_ctx.$slots, "default", _normalizeProps(_guardReactiveProps(inputAttrs))),
        _renderSlot(_ctx.$slots, "inputRight", _normalizeProps(_guardReactiveProps(inputRightAttrs)))
      ]),
      _renderSlot(_ctx.$slots, "inputAfter", _normalizeProps(_guardReactiveProps(inputAfterAttrs)))
    ]),
    (_openBlock(), _createBlock(_resolveDynamicComponent(errorTransitionComponent.value), null, {
      default: _withCtx(() => [
        (_ctx.errorMessage || _ctx.supportingText)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: _normalizeClass(["ds-form-field__footer", { 'ds-form-field__footer--floating': _ctx.floatingError && !_ctx.supportingText }]),
              innerHTML: _ctx.errorMessage || _ctx.supportingText,
              role: "alert",
              "aria-live": ariaLive.value,
              "aria-atomic": "true"
            }, null, 10, _hoisted_6))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }))
  ], 2))
}
}

})